import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, Lock, Shield, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login, logout, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        await logout();
        toast.error("Access denied. This portal is for administrators only.");
        return;
      }
      toast.success(`Welcome back, ${u.name}!`);
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("AdminLogin error:", status, detail, err.message);
      let msg = formatApiError(detail);
      if (!detail) {
        if (status === 401) msg = "Invalid email or password.";
        else if (status === 403) msg = "Your account is not authorised.";
        else if (!err.response) msg = "Cannot reach server. Check your connection.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12 overflow-hidden">
      {/* Dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0b1637] via-[#1E3A8A] to-[#0b1637]" />
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "radial-gradient(circle at 20% 20%, rgba(245, 158, 11, 0.3), transparent 40%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.3), transparent 40%)",
      }} />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cpath d='M0 0h60v60H0z' fill='none'/%3E%3Cpath d='M30 0v60M0 30h60' stroke='white' stroke-width='0.5'/%3E%3C/svg%3E\")",
      }} />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6" data-testid="admin-login-brand-link">
          <Logo size={56} textClassName="text-white" />
        </Link>

        <div className="bg-white/[0.08] backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
              <Shield size={20} className="text-amber-300" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">Restricted Access</div>
              <h1 className="heading text-xl font-black text-white leading-tight">Admin Portal</h1>
            </div>
          </div>
          <p className="text-sm text-slate-300 mt-3">
            Login with your administrator credentials to access the PMR Welfare Foundation control panel.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Admin Email</label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-3.5 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@pmrwelfare.org"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:border-amber-400 focus:bg-white/[0.15] outline-none transition"
                  data-testid="admin-login-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Password</label>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-3.5 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:border-amber-400 focus:bg-white/[0.15] outline-none transition"
                  data-testid="admin-login-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0b1637] font-black rounded-lg py-3 flex items-center justify-center gap-2 transition shadow-lg shadow-amber-500/30"
              data-testid="admin-login-submit-button"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Sign in to Admin Panel
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-white/10">
            <div className="text-[11px] text-slate-400 leading-relaxed">
              This portal is exclusively for authorized administrators of PMR Welfare Foundation. All login attempts are logged. Unauthorized access is strictly prohibited.
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-4 text-xs text-slate-300">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-amber-300 transition" data-testid="admin-login-back-home">
            <ArrowLeft size={12} /> Back to website
          </Link>
          <span className="text-slate-500">•</span>
          <Link to="/login" className="hover:text-amber-300 transition" data-testid="admin-login-to-user-login">
            Student / Volunteer login
          </Link>
        </div>
      </div>
    </div>
  );
}
